import React, { useState, useEffect, useMemo } from 'react';
import { 
  View, Text, StyleSheet, FlatList, TouchableOpacity, 
  RefreshControl, TextInput, LayoutAnimation,
  Linking, ActivityIndicator, ScrollView
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AdminLayout from '../components/layout/AdminLayout';
import { getLMSProgressTracking } from '../api/analytics';
import { getColor } from '../theme/adminTheme';
import { FINAL_API_URL } from '../../services/api';

const AcademicBadge = ({ status }) => {
  const isCompleted = status === 'completed';
  return (
    <View style={[
      styles.statusBadge, 
      { backgroundColor: isCompleted ? '#DEF7EC' : '#E1EFFE', borderColor: isCompleted ? '#A7F3D0' : '#BFDBFE', borderWidth: 1 }
    ]}>
      <View style={[
        styles.statusDot, 
        { backgroundColor: isCompleted ? '#059669' : '#1C64F2' }
      ]} />
      <Text style={[
        styles.statusText, 
        { color: isCompleted ? '#03543F' : '#1E429F' }
      ]}>
        {isCompleted ? 'Completed' : 'In Progress'}
      </Text>
    </View>
  );
};

const ExpandableUserCard = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedCourses, setExpandedCourses] = useState({});

  const toggleExpand = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpanded(!expanded);
  };

  const toggleCourseExpand = (courseId) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedCourses(prev => ({
      ...prev,
      [courseId]: !prev[courseId]
    }));
  };

  const progress = item.lmsProgress || {};
  const courses = progress.courses || [];
  const certs = progress.certificatesList || [];
  const completedCount = progress.completed || 0;
  const enrolledCount = progress.enrolled || 0;

  const getProxyUrl = (type, lmsUserId, courseId = null) => {
    let url = `${FINAL_API_URL}admin/lms/proxy-view/${lmsUserId}/${type}`;
    if (courseId) url += `?courseId=${courseId}`;
    return url;
  };

  return (
    <View style={styles.card}>
      <TouchableOpacity 
        style={[styles.cardHeader, expanded && styles.cardHeaderExpanded]} 
        onPress={toggleExpand}
        activeOpacity={0.7}
      >
        <View style={styles.avatarContainer}>
          <Text style={styles.avatarText}>{item.name.charAt(0)}</Text>
        </View>
        <View style={styles.headerInfo}>
          <Text style={styles.nameText}>{item.name}</Text>
          <Text style={styles.emailText}>{item.email}</Text>
        </View>

        <View style={styles.statsSummary}>
          <Ionicons name="library-outline" size={14} color="#6B7280" style={{marginRight: 4}} />
          <Text style={styles.summaryValue}>{completedCount}/{enrolledCount} Courses</Text>
        </View>

        <View style={styles.expandIconCircle}>
           <Ionicons 
             name={expanded ? "chevron-up" : "chevron-down"} 
             size={16} 
             color="#6B7280" 
           />
        </View>
      </TouchableOpacity>

      {expanded && (
        <View style={styles.expandedContent}>
          
          {certs.length > 0 && (
            <View style={styles.certsContainer}>
              <View style={styles.subHeader}>
                <Ionicons name="ribbon-outline" size={14} color="#6B7280" />
                <Text style={styles.subTitle}>Credentials</Text>
              </View>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.certsScroll}>
                {certs.map((cert) => (
                  <TouchableOpacity 
                    key={cert.id} 
                    style={styles.certCard}
                    onPress={() => cert.pdfUrl && Linking.openURL(cert.pdfUrl)}
                  >
                    <MaterialCommunityIcons name="certificate-outline" size={18} color="#D97706" />
                    <View style={styles.certInfo}>
                      <Text style={styles.certName} numberOfLines={1}>{cert.course?.name || 'Certificate'}</Text>
                      <Text style={styles.certDate}>{cert.issueDate || 'Issued'}</Text>
                    </View>
                    <Ionicons name="download-outline" size={14} color="#4B5563" />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}

          <View style={[styles.subHeader, certs.length > 0 && { marginTop: 8 }]}>
            <Ionicons name="analytics-outline" size={14} color="#6B7280" />
            <Text style={styles.subTitle}>Learning Journey</Text>
          </View>

          {courses.length === 0 ? (
            <View style={styles.emptyContainer}>
               <Text style={styles.emptyText}>No active learning data synced.</Text>
            </View>
          ) : (
            courses.map((course) => (
              <View key={course.courseId} style={styles.courseCard}>
                <TouchableOpacity 
                  style={styles.courseHeader}
                  onPress={() => toggleCourseExpand(course.courseId)}
                  activeOpacity={0.7}
                >
                  <View style={styles.courseTitleRow}>
                     <AcademicBadge status={course.overallStatus} />
                     <Text style={styles.courseNameText}>{course.courseName}</Text>
                  </View>
                  
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                    <TouchableOpacity 
                      style={styles.transcriptBtn}
                      onPress={() => {
                          const lmsId = progress.lmsUserId || progress.id || item.email;
                          Linking.openURL(getProxyUrl('transcript', lmsId, course.courseId));
                      }}
                    >
                      <Ionicons name="document-text-outline" size={14} color="#4B5563" />
                      <Text style={styles.transcriptBtnText}>Transcript</Text>
                    </TouchableOpacity>
                    <Ionicons 
                      name={expandedCourses[course.courseId] ? "chevron-up" : "chevron-down"} 
                      size={16} 
                      color="#9CA3AF" 
                    />
                  </View>
                </TouchableOpacity>

                <View style={styles.progressSection}>
                  <View style={styles.progressInfo}>
                    <Text style={styles.progressDetail}>
                      Completion: <Text style={{ fontWeight: '600' }}>{course.progressPercent}%</Text>
                    </Text>
                    <Text style={styles.unitCount}>
                      {course.units?.filter(u => u.status === 'completed').length || 0}/{course.units?.length || 0} Units
                    </Text>
                  </View>
                  <View style={styles.largeProgressBarBg}>
                    <View 
                      style={[
                        styles.largeProgressBarFill, 
                        { width: `${course.progressPercent || 0}%` },
                        course.overallStatus === 'completed' && { backgroundColor: '#10B981' }
                      ]} 
                    />
                  </View>
                </View>

                {expandedCourses[course.courseId] && (
                  <View style={styles.unitList}>
                    {course.units && course.units.map((unit, idx) => (
                      <View key={idx} style={styles.unitItem}>
                        <View style={[
                          styles.unitStatusIcon,
                          { backgroundColor: unit.status === 'completed' ? '#D1FAE5' : '#F3F4F6' }
                        ]}>
                          <Ionicons 
                            name={unit.status === 'completed' ? "checkmark" : "time-outline"} 
                            size={12} 
                            color={unit.status === 'completed' ? '#059669' : '#6B7280'} 
                          />
                        </View>
                        <Text style={[styles.unitName, unit.status === 'completed' && { color: '#111827', fontWeight: '500' }]} numberOfLines={1}>
                          {unit.name}
                        </Text>
                        {unit.grade !== null && (
                          <View style={[
                            styles.gradeBadge,
                            { backgroundColor: unit.grade >= 50 ? '#EFF6FF' : '#FEF2F2' }
                          ]}>
                            <Text style={[
                              styles.gradeText,
                              { color: unit.grade >= 50 ? '#1D4ED8' : '#B91C1C' }
                            ]}>{unit.grade}%</Text>
                          </View>
                        )}
                      </View>
                    ))}
                  </View>
                )}
              </View>
            ))
          )}

          <View style={styles.cardFooter}>
            <Ionicons name="sync-outline" size={10} color="#9CA3AF" />
            <Text style={styles.lastSyncedText}>
              Synced: {new Date(item.updatedAt).toLocaleDateString()} {new Date(item.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const LMSProgressTrackingScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getLMSProgressTracking();
      if (response?.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching LMS progress:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u => 
      u.name.toLowerCase().includes(q) || 
      u.email.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  return (
    <AdminLayout>
      <View style={styles.mainContainer}>
        <View style={styles.pageHeader}>
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => navigation.goBack()} style={styles.iconButton}>
              <Ionicons name="chevron-back" size={20} color="#1F2937" />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.pageTitle}>Learning Analytics</Text>
              <Text style={styles.pageSubtitle}>Monitor student progress & certificates</Text>
            </View>
            <TouchableOpacity onPress={onRefresh} style={styles.iconButton}>
              <Ionicons name="refresh" size={18} color="#4B5563" />
            </TouchableOpacity>
          </View>

          <View style={styles.searchContainer}>
            <View style={styles.searchInputWrapper}>
              <Ionicons name="search" size={18} color="#9CA3AF" />
              <TextInput
                style={styles.mainSearchInput}
                placeholder="Search by student name or email..."
                placeholderTextColor="#9CA3AF"
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <Ionicons name="close-circle" size={18} color="#9CA3AF" />
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>

        {loading && !refreshing ? (
          <View style={styles.centered}>
            <ActivityIndicator size="small" color="#2563EB" />
            <Text style={styles.loadingText}>Fetching academic data...</Text>
          </View>
        ) : (
          <FlatList
            data={filteredUsers}
            renderItem={({ item }) => <ExpandableUserCard item={item} />}
            keyExtractor={(item) => item.id.toString()}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#2563EB" />
            }
            ListHeaderComponent={
              <View style={styles.listHeader}>
                 <Text style={styles.resultCount}>{filteredUsers.length} Students Synced</Text>
              </View>
            }
            ListEmptyComponent={
              <View style={styles.emptyStateContainer}>
                <Ionicons name="school-outline" size={48} color="#D1D5DB" />
                <Text style={styles.emptyStateTitle}>No Students Found</Text>
                <Text style={styles.emptyStateSub}>Try adjusting your search or check again later.</Text>
              </View>
            }
          />
        )}
      </View>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  mainContainer: { flex: 1, backgroundColor: '#F3F4F6' },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 12, color: '#6B7280', fontSize: 13, fontWeight: '500' },
  
  pageHeader: { backgroundColor: '#FFF', paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  headerTop: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 12 },
  iconButton: { padding: 6, borderRadius: 8, backgroundColor: '#F9FAFB', borderWidth: 1, borderColor: '#E5E7EB' },
  headerTitleContainer: { flex: 1, marginHorizontal: 12 },
  pageTitle: { fontSize: 18, fontWeight: '700', color: '#111827' },
  pageSubtitle: { fontSize: 12, color: '#6B7280' },
  
  searchContainer: { paddingHorizontal: 16, marginTop: 12 },
  searchInputWrapper: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    backgroundColor: '#FFF', 
    borderRadius: 8, 
    paddingHorizontal: 12,
    height: 40,
    borderWidth: 1,
    borderColor: '#D1D5DB'
  },
  mainSearchInput: { flex: 1, marginHorizontal: 8, fontSize: 14, color: '#111827' },

  listContainer: { padding: 16, paddingBottom: 40 },
  listHeader: { marginBottom: 12, paddingHorizontal: 4 },
  resultCount: { fontSize: 13, fontWeight: '600', color: '#4B5563' },

  card: { backgroundColor: '#FFF', borderRadius: 8, marginBottom: 12, borderWidth: 1, borderColor: '#E5E7EB', overflow: 'hidden' },
  cardHeader: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 16, backgroundColor: '#FFF' },
  cardHeaderExpanded: { borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  avatarContainer: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#EFF6FF', justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: '#BFDBFE' },
  avatarText: { fontSize: 14, fontWeight: '700', color: '#1D4ED8' },
  headerInfo: { flex: 1, marginLeft: 12 },
  nameText: { fontSize: 14, fontWeight: '600', color: '#111827' },
  emailText: { fontSize: 12, color: '#6B7280', marginTop: 2 },
  statsSummary: { flexDirection: 'row', alignItems: 'center', marginRight: 12, backgroundColor: '#F3F4F6', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  summaryValue: { fontSize: 11, fontWeight: '600', color: '#4B5563' },
  expandIconCircle: { width: 24, height: 24, borderRadius: 12, backgroundColor: '#F9FAFB', justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: '#E5E7EB' },

  expandedContent: { backgroundColor: '#F9FAFB', padding: 16 },
  subHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  subTitle: { fontSize: 11, fontWeight: '700', color: '#6B7280', marginLeft: 6, textTransform: 'uppercase', letterSpacing: 0.5 },

  certsContainer: { marginBottom: 16 },
  certsScroll: { marginTop: 4 },
  certCard: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    backgroundColor: '#FFF', 
    paddingHorizontal: 12, 
    paddingVertical: 10,
    borderRadius: 8, 
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  certInfo: { marginLeft: 8, marginRight: 16 },
  certName: { fontSize: 12, fontWeight: '600', color: '#111827', maxWidth: 140 },
  certDate: { fontSize: 10, color: '#6B7280', marginTop: 2 },

  courseCard: { backgroundColor: '#FFF', borderRadius: 8, padding: 12, marginBottom: 10, borderWidth: 1, borderColor: '#E5E7EB' },
  courseHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  courseTitleRow: { flex: 1, flexDirection: 'row', alignItems: 'center', marginRight: 10 },
  courseNameText: { fontSize: 13, fontWeight: '600', color: '#111827', marginLeft: 8, flexShrink: 1 },
  transcriptBtn: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    backgroundColor: '#FFF', 
    borderWidth: 1,
    borderColor: '#D1D5DB',
    paddingHorizontal: 8, 
    paddingVertical: 4, 
    borderRadius: 6 
  },
  transcriptBtnText: { color: '#374151', fontSize: 11, fontWeight: '600', marginLeft: 4 },

  progressSection: { marginTop: 12, backgroundColor: '#F9FAFB', padding: 10, borderRadius: 6, borderWidth: 1, borderColor: '#F3F4F6' },
  progressInfo: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  progressDetail: { fontSize: 11, color: '#6B7280' },
  unitCount: { fontSize: 11, color: '#6B7280', fontWeight: '500' },
  largeProgressBarBg: { height: 6, backgroundColor: '#E5E7EB', borderRadius: 3, overflow: 'hidden' },
  largeProgressBarFill: { height: '100%', backgroundColor: '#2563EB', borderRadius: 3 },

  unitList: { marginTop: 10, borderTopWidth: 1, borderTopColor: '#F3F4F6', paddingTop: 8, gap: 4 },
  unitItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4 },
  unitStatusIcon: { width: 20, height: 20, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  unitName: { flex: 1, marginLeft: 8, fontSize: 12, color: '#4B5563' },
  gradeBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 },
  gradeText: { fontSize: 10, fontWeight: '700' },

  statusBadge: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 12 },
  statusDot: { width: 6, height: 6, borderRadius: 3, marginRight: 4 },
  statusText: { fontSize: 10, fontWeight: '600' },

  cardFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 12, borderTopWidth: 1, borderTopColor: '#F3F4F6', paddingTop: 12 },
  lastSyncedText: { fontSize: 10, color: '#9CA3AF', marginLeft: 4 },

  emptyStateContainer: { alignItems: 'center', marginTop: 60, paddingHorizontal: 40 },
  emptyStateTitle: { fontSize: 16, fontWeight: '600', color: '#4B5563', marginTop: 16 },
  emptyStateSub: { fontSize: 13, color: '#9CA3AF', textAlign: 'center', marginTop: 8 },
  emptyContainer: { padding: 16, alignItems: 'center', backgroundColor: '#FFF', borderRadius: 8, borderWidth: 1, borderColor: '#E5E7EB', borderStyle: 'dashed' },
  emptyText: { color: '#6B7280', fontStyle: 'italic', fontSize: 12 },
});

export default LMSProgressTrackingScreen;
